import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}

(() => {
  const EXCLUDED_RECOMMENDATION_TAG = 'hide-from-cart-recommendations';
  const RECOMMENDATION_LIMIT = 6;
  const SAME_AGE_LIMIT = 3;
  const PRODUCT_POOL_LIMIT = 250;
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  let pendingStickyAdd = false;

  function injectStyles() {
    if (document.getElementById('luca-sticky-recommendations-styles')) return;
    const style = document.createElement('style');
    style.id = 'luca-sticky-recommendations-styles';
    style.textContent = `
      @media screen and (max-width: 749px) {
        body:has(sticky-add-to-cart) { padding-bottom: calc(78px + env(safe-area-inset-bottom)); }
        #es-whatsapp-chat-embed a { bottom: calc(86px + env(safe-area-inset-bottom)) !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__bar,
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__bar[data-stuck='false'],
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__bar[data-stuck='true'] {
          position: fixed !important; right: 0 !important; bottom: 0 !important; left: 0 !important; z-index: 80 !important;
          display: grid !important; grid-template-columns: 52px minmax(0, 1fr) auto !important; grid-template-rows: auto auto !important;
          column-gap: 9px !important; row-gap: 2px !important; align-items: center !important;
          width: 100% !important; max-width: none !important; opacity: 1 !important; transform: translateY(0) !important;
          border-radius: 0 !important; padding: 8px 10px max(8px, env(safe-area-inset-bottom)) !important;
          background: rgb(var(--color-background-rgb, 255 255 255)) !important; border: 1px solid rgb(var(--color-foreground-rgb, 18 18 18) / 0.1) !important;
          box-shadow: 0 -10px 28px rgb(var(--color-shadow-rgb, 0 0 0) / 0.14) !important;
        }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__bar::before { display: none !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__image { grid-column: 1 !important; grid-row: 1 / 3 !important; height: 52px !important; border-radius: 10px !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__info { grid-column: 2 !important; grid-row: 1 !important; width: 100% !important; align-self: end !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__info[data-has-image='false'] { grid-column: 1 / 3 !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__title { display: block !important; width: 100% !important; font-size: 13px !important; font-weight: 800 !important; line-height: 1.25 !important; overflow: hidden !important; text-overflow: ellipsis !important; white-space: nowrap !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__variant { display: none !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__price { grid-column: 2 !important; grid-row: 2 !important; align-self: start !important; justify-self: start !important; color: #ef0000 !important; font-size: 15px !important; font-weight: 900 !important; line-height: 1.2 !important; white-space: nowrap !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__info[data-has-image='false'] + .sticky-add-to-cart__price { grid-column: 1 / 3 !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__price .price__sale { display: inline-flex !important; align-items: baseline !important; gap: 6px !important; direction: rtl !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__price .price,
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__price .price-item--sale { color: #ef0000 !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__price .compare-at-price { color: #5f5f5f !important; font-weight: 700 !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__button { grid-column: 3 !important; grid-row: 1 / 3 !important; width: auto !important; min-width: 112px !important; height: 48px !important; border-radius: 11px !important; padding: 0 14px !important; font-size: 14px !important; font-weight: 900 !important; white-space: nowrap !important; }
        sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__bar .add-to-cart-text__content { display: inline-flex !important; }
      }
      .luca-benefits-sheet[hidden] { display: none !important; }
      .luca-benefits-sheet { position: fixed; inset: 0; z-index: 9999; font-family: var(--font-body--family); }
      .luca-benefits-sheet__scrim { position: absolute; inset: 0; width: 100%; border: 0; background: rgb(0 0 0 / 0.46); opacity: 0; transition: opacity 0.24s ease; }
      .luca-benefits-sheet__panel { position: absolute; right: 0; bottom: 0; left: 0; max-height: min(82dvh, 680px); overflow: hidden; border-radius: 20px 20px 0 0; background: rgb(var(--color-background-rgb, 255 255 255)); color: rgb(var(--color-foreground-rgb, 18 18 18)); transform: translateY(100%); transition: transform 0.24s var(--ease-out-quad, ease); box-shadow: 0 -18px 44px rgb(0 0 0 / 0.2); padding: 16px 14px max(18px, env(safe-area-inset-bottom)); display: flex; flex-direction: column; }
      .luca-benefits-sheet[data-open='true'] .luca-benefits-sheet__scrim { opacity: 1; }
      .luca-benefits-sheet[data-open='true'] .luca-benefits-sheet__panel { transform: translateY(0); }
      .luca-benefits-sheet__success { display: grid; grid-template-columns: 54px minmax(0, 1fr) auto; gap: 10px; align-items: center; margin: -2px -2px 12px; padding: 4px 4px 14px; border-bottom: 1px solid rgb(var(--color-foreground-rgb, 18 18 18) / 0.1); flex: 0 0 auto; }
      .luca-benefits-sheet__close { grid-column: 3; grid-row: 1; width: 36px; height: 36px; border: 0; border-radius: 50%; background: rgb(var(--color-foreground-rgb, 18 18 18) / 0.06); color: inherit; font: inherit; font-size: 26px; line-height: 1; }
      .luca-benefits-sheet__added-image { grid-column: 1; grid-row: 1; width: 54px; height: 54px; border-radius: 10px; object-fit: contain; background: rgb(var(--color-foreground-rgb, 18 18 18) / 0.04); }
      .luca-benefits-sheet__success-title, .luca-benefits-sheet__added-title, .luca-benefits-sheet__added-price { margin: 0; }
      .luca-benefits-sheet__success-title { color: #238052; font-size: 17px; font-weight: 900; line-height: 1.25; }
      .luca-benefits-sheet__added-title { margin-top: 3px; overflow: hidden; color: rgb(var(--color-foreground-rgb, 18 18 18) / 0.72); font-size: 12px; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
      .luca-benefits-sheet__added-price { color: #6f559f; font-size: 13px; font-weight: 900; }
      .luca-benefits-sheet__heading { margin: 0 0 12px; color: #24212a; font-size: 19px; font-weight: 900; line-height: 1.25; text-align: center; flex: 0 0 auto; }
      .luca-benefits-sheet__list { display: grid; gap: 10px; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; padding: 0 2px 2px; flex: 1 1 auto; min-height: 0; max-height: min(46dvh, 430px); }
      .luca-benefits-sheet__item, .luca-benefits-sheet__loading, .luca-benefits-sheet__empty { border: 1px solid rgb(var(--color-foreground-rgb, 18 18 18) / 0.1); border-radius: 10px; background: #ffffff; }
      .luca-benefits-sheet__item { display: grid; grid-template-columns: 74px minmax(0, 1fr); gap: 10px 12px; align-items: center; padding: 10px; }
      .luca-benefits-sheet__item-media { grid-row: span 2; aspect-ratio: 1; overflow: hidden; border-radius: 10px; background: #f8f5fb; }
      .luca-benefits-sheet__item-image { width: 100%; height: 100%; object-fit: contain; }
      .luca-benefits-sheet__item-info { min-width: 0; }
      .luca-benefits-sheet__item-title { display: block; overflow: hidden; color: #24212a; font-size: 15px; font-weight: 800; line-height: 1.3; text-decoration: none; text-overflow: ellipsis; white-space: nowrap; }
      .luca-benefits-sheet__item-price { display: block; margin-top: 4px; color: #ef0000; font-size: 15px; font-weight: 900; }
      .luca-benefits-sheet__item-button, .luca-benefits-sheet__cart { min-height: 44px; border-radius: 9px; font: inherit; font-size: 16px; font-weight: 900; text-align: center; }
      .luca-benefits-sheet__item-button { width: 100%; border: 1px solid #6f559f; background: #ffffff; color: #6f559f; transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease, transform 0.18s ease; }
      .luca-benefits-sheet__item-button:disabled { opacity: 0.78; }
      .luca-benefits-sheet__item-button.is-added { border-color: #238052; background: #238052; color: #ffffff; transform: scale(0.98); }
      .luca-benefits-sheet__loading, .luca-benefits-sheet__empty { padding: 18px; color: rgb(var(--color-foreground-rgb, 18 18 18) / 0.7); font-weight: 800; text-align: center; }
      .luca-benefits-sheet__cart { display: flex; align-items: center; justify-content: center; margin-top: 14px; background: #6f559f; color: #ffffff; text-decoration: none; flex: 0 0 auto; }
      @media screen and (min-width: 750px) { .luca-benefits-sheet { display: none !important; } }
    `;
    document.head.appendChild(style);
  }

  function forceStickyState() {
    document.querySelectorAll('sticky-add-to-cart .sticky-add-to-cart__bar').forEach((bar) => {
      bar.dataset.stuck = 'true';
    });
  }

  function updateStickyButtonLabel() {
    document
      .querySelectorAll(
        "sticky-add-to-cart[data-variant-available='true'] .sticky-add-to-cart__button .add-to-cart-text__content > span:first-child"
      )
      .forEach((label) => {
        label.textContent = 'הוספה לסל';
      });
  }

  function sheet() {
    let element = document.querySelector('[data-luca-benefits-sheet]');
    if (element) return element;
    element = document.createElement('div');
    element.className = 'luca-benefits-sheet';
    element.dataset.lucaBenefitsSheet = 'true';
    element.hidden = true;
    element.dir = 'rtl';
    element.innerHTML = `
      <button class="luca-benefits-sheet__scrim" type="button" data-close-luca-sheet aria-label="סגירה"></button>
      <div class="luca-benefits-sheet__panel" role="dialog" aria-modal="true" aria-label="לקוחות שקנו מוצר זה התעניינו גם">
        <div class="luca-benefits-sheet__success">
          <button class="luca-benefits-sheet__close" type="button" data-close-luca-sheet aria-label="סגירה">×</button>
          <img class="luca-benefits-sheet__added-image" data-added-image alt="" loading="lazy">
          <div>
            <p class="luca-benefits-sheet__success-title">המוצר נוסף בהצלחה לעגלה</p>
            <p class="luca-benefits-sheet__added-title" data-added-title></p>
            <p class="luca-benefits-sheet__added-price" data-added-price></p>
          </div>
        </div>
        <h2 class="luca-benefits-sheet__heading">לקוחות שקנו מוצר זה התעניינו גם:</h2>
        <div class="luca-benefits-sheet__list" data-recommendations-list></div>
        <a class="luca-benefits-sheet__cart" href="/cart">למעבר לעגלת הקניות</a>
      </div>
    `;
    element.addEventListener('click', handleSheetClick);
    document.body.appendChild(element);
    return element;
  }

  function closeSheet(element) {
    element.dataset.open = 'false';
    window.setTimeout(() => { element.hidden = true; }, 240);
  }

  function updateCartBubble(itemCount) {
    const count = Number(itemCount) || 0;
    const label = count > 99 ? '99+' : `${count}`;

    document.querySelectorAll('[ref="cartBubble"], .cart-bubble').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', count < 1);
    });

    document.querySelectorAll('[ref="cartBubbleCount"], .cart-bubble__text-count, [data-cart-count]').forEach((counter) => {
      counter.textContent = label;
      counter.classList.toggle('hidden', count < 1);
    });

    document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((counter) => {
      counter.textContent = label;
    });
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  }

  function notifyCartUpdated(cart) {
    updateCartBubble(cart?.item_count || 0);
    document.dispatchEvent(new CartLinesUpdateEvent({
      action: 'update',
      context: 'cart',
      lines: cart.items.map((item) => ({ id: item.key, quantity: item.quantity })),
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: 'luca-recommendations', items: cart.items }
      })
    }));
  }

  async function handleSheetClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const root = target.closest('[data-luca-benefits-sheet]');
    if (!root) return;
    if (target.closest('[data-close-luca-sheet]')) {
      closeSheet(root);
      return;
    }
    const button = target.closest('[data-recommendation-variant]');
    if (!(button instanceof HTMLButtonElement)) return;
    const variantId = button.dataset.recommendationVariant;
    if (!variantId) return;
    button.disabled = true;
    button.classList.remove('is-added');
    button.textContent = 'מוסיף...';
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
      });
      if (!response.ok) throw new Error('Cart add failed');
      const cart = await fetchCart();
      notifyCartUpdated(cart);
      button.classList.add('is-added');
      button.textContent = 'נוסף';
    } catch (_error) {
      button.disabled = false;
      button.classList.remove('is-added');
      button.textContent = 'הוספה';
    }
  }

  function currentProductHandle() {
    const match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  async function fetchJson(url) {
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Request failed: ${url}`);
    return response.json();
  }

  function productTags(product) {
    if (!product) return [];
    if (Array.isArray(product.tags)) return product.tags.map((tag) => `${tag}`);
    if (typeof product.tags === 'string') return product.tags.split(',').map((tag) => tag.trim());
    return [];
  }

  function hasExcludedRecommendationTag(product) {
    return productTags(product).some((tag) => tag.trim().toLowerCase() === EXCLUDED_RECOMMENDATION_TAG);
  }

  function ageKeys(product) {
    const keys = new Set();
    productTags(product).forEach((rawTag) => {
      const tag = rawTag.toLowerCase().replace(/[_\s]+/g, '-').trim();
      const readableTag = rawTag.toLowerCase().replace(/\s+/g, ' ').trim();

      if (/age-0-3|0\s*-\s*3/.test(tag) || /0\s*-\s*3/.test(readableTag)) keys.add('0-3');
      if (/age-2-plus|2\+|גיל\s*2\+|מגיל\s*2/.test(tag) || /גיל\s*2\+|מגיל\s*2/.test(readableTag)) keys.add('2+');
      if (/age-3-plus|3\+|גיל\s*3\+|מגיל\s*3/.test(tag) || /גיל\s*3\+|מגיל\s*3/.test(readableTag)) keys.add('3+');
      if (/age-4-plus|4\+|גיל\s*4\+|מגיל\s*4/.test(tag) || /גיל\s*4\+|מגיל\s*4/.test(readableTag)) keys.add('4+');
      if (/age-6-plus|6\+|גיל\s*6\+|מגיל\s*6/.test(tag) || /גיל\s*6\+|מגיל\s*6/.test(readableTag)) keys.add('6+');
      if (/age-8-plus|8\+|גיל\s*8\+|מגיל\s*8/.test(tag) || /גיל\s*8\+|מגיל\s*8/.test(readableTag)) keys.add('8+');
      if (/age-14-plus|14\+|גיל\s*14\+|מגיל\s*14/.test(tag) || /גיל\s*14\+|מגיל\s*14/.test(readableTag)) keys.add('14+');
    });
    return Array.from(keys);
  }

  function normalizeProduct(product) {
    const variant = (product.variants || []).find((item) => item.available) || product.variants?.[0];
    return variant ? { ...product, selectedVariant: variant } : null;
  }

  function uniqueProducts(products) {
    const seen = new Set();
    return products.filter((product) => {
      if (!product?.handle || seen.has(product.handle)) return false;
      seen.add(product.handle);
      return true;
    });
  }

  async function fetchProductPool() {
    const data = await fetchJson(`/products.json?limit=${PRODUCT_POOL_LIMIT}`);
    return uniqueProducts((data.products || []).map(normalizeProduct).filter(Boolean));
  }

  function productDate(product) {
    return Date.parse(product.created_at || product.published_at || product.updated_at || '') || 0;
  }

  function ageMatchScore(product, currentAges) {
    const productAges = ageKeys(product);
    return productAges.reduce((score, age) => score + (currentAges.includes(age) ? 1 : 0), 0);
  }

  async function fetchProducts(currentVariantId) {
    const handle = currentProductHandle();
    let currentProduct = null;
    try {
      if (handle) currentProduct = await fetchJson(`/products/${handle}.js`);
    } catch (_error) {}

    let products = [];
    try {
      products = await fetchProductPool();
    } catch (_error) {}

    if (!products.length) {
      try {
        const data = await fetchJson(`/collections/cart-upsell/products.json?limit=${PRODUCT_POOL_LIMIT}`);
        products = uniqueProducts((data.products || []).map(normalizeProduct).filter(Boolean));
      } catch (_error) {}
    }

    const currentAges = ageKeys(currentProduct);
    const blocked = new Set([`${currentVariantId}`, currentProduct?.handle, handle].filter(Boolean));
    const available = products.filter((product) => {
      const key = `${product.selectedVariant.id}`;
      if (blocked.has(key) || blocked.has(product.handle)) return false;
      if (hasExcludedRecommendationTag(product)) return false;
      return true;
    });

    const sameAge = currentAges.length
      ? available
          .map((product) => ({ product, score: ageMatchScore(product, currentAges) }))
          .filter((item) => item.score > 0)
          .sort((a, b) => b.score - a.score || productDate(b.product) - productDate(a.product))
          .map((item) => item.product)
          .slice(0, SAME_AGE_LIMIT)
      : [];

    sameAge.forEach((product) => {
      blocked.add(`${product.selectedVariant.id}`);
      blocked.add(product.handle);
    });

    const rest = available
      .filter((product) => !blocked.has(`${product.selectedVariant.id}`) && !blocked.has(product.handle))
      .sort((a, b) => productDate(b) - productDate(a));

    return sameAge.concat(rest).slice(0, RECOMMENDATION_LIMIT);
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value || '';
    return div.innerHTML;
  }

  function formatPrice(value) {
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) return '';
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS', maximumFractionDigits: numberValue % 1 === 0 ? 0 : 2 }).format(numberValue);
  }

  function renderProduct(product) {
    const variant = product.selectedVariant;
    const title = escapeHtml(product.title);
    const image = product.images?.[0]?.src || product.image?.src || '';
    const img = image ? `<img class="luca-benefits-sheet__item-image" src="${escapeHtml(image)}" alt="${title}" loading="lazy">` : '';
    return `
      <div class="luca-benefits-sheet__item">
        <a class="luca-benefits-sheet__item-media" href="/products/${escapeHtml(product.handle)}">${img}</a>
        <div class="luca-benefits-sheet__item-info">
          <a class="luca-benefits-sheet__item-title" href="/products/${escapeHtml(product.handle)}">${title}</a>
          <span class="luca-benefits-sheet__item-price">${formatPrice(variant.price)}</span>
        </div>
        <button class="luca-benefits-sheet__item-button" type="button" data-recommendation-variant="${variant.id}">הוספה</button>
      </div>
    `;
  }

  async function openSheet() {
    const sticky = document.querySelector('sticky-add-to-cart');
    const element = sheet();
    const title = sticky?.querySelector('.sticky-add-to-cart__title')?.textContent?.trim() || '';
    const price = sticky?.querySelector('.sticky-add-to-cart__price')?.textContent?.trim() || '';
    const image = sticky?.querySelector('.sticky-add-to-cart__image-img')?.currentSrc || sticky?.querySelector('.sticky-add-to-cart__image-img')?.src || '';
    const currentVariantId = sticky?.dataset.currentVariantId || '';

    element.querySelector('[data-added-title]').textContent = title;
    element.querySelector('[data-added-price]').textContent = price;
    const imageElement = element.querySelector('[data-added-image]');
    if (imageElement instanceof HTMLImageElement && image) {
      imageElement.src = image;
      imageElement.alt = title;
    }

    const list = element.querySelector('[data-recommendations-list]');
    list.innerHTML = '<div class="luca-benefits-sheet__loading">טוען מוצרים...</div>';
    const products = await fetchProducts(currentVariantId);
    list.innerHTML = products.length ? products.map(renderProduct).join('') : '<div class="luca-benefits-sheet__empty">המשיכו לעגלה כדי להשלים את ההזמנה.</div>';

    element.hidden = false;
    requestAnimationFrame(() => { element.dataset.open = 'true'; });
  }

  injectStyles();
  forceStickyState();
  updateStickyButtonLabel();
  document.addEventListener('DOMContentLoaded', () => {
    forceStickyState();
    updateStickyButtonLabel();
  });
  document.addEventListener(StandardEvents.productSelect, (event) => {
    event.promise?.then(() => { forceStickyState(); updateStickyButtonLabel(); }).catch(() => {});
  });
  document.addEventListener('click', (event) => {
    if (!isMobile()) return;
    if (event.target instanceof Element && event.target.closest('sticky-add-to-cart .sticky-add-to-cart__button')) {
      pendingStickyAdd = true;
    }
  }, true);
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (!pendingStickyAdd || !isMobile() || event.action !== 'add') return;
    pendingStickyAdd = false;
    event.promise?.then((result) => {
      if (!result.detail?.didError && !result.userErrors?.length) openSheet();
    }).catch(() => {});
  });
  document.addEventListener(StandardEvents.cartError, () => { pendingStickyAdd = false; });
})();
